<template>
    <div class="logo-container" :class="classes" v-bind="$attrs">
        <svg v-show="!isDarkMode" viewBox="0 0 240 240" class="w-40 h-40 mb-6 drop-shadow-sm" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Hexagon Shield (Indigo 600) -->
            <path d="M120 15 L210 65 L210 175 L120 225 L30 175 L30 65 Z" stroke="#4F46E5" stroke-width="12" stroke-linejoin="round"></path>
            
            <!-- Bug Body / I Shape -->
            <rect x="108" y="85" width="24" height="60" rx="12" fill="#111827"></rect>
            <circle cx="120" cy="65" r="14" fill="#111827"></circle>
            
            <!-- Bug Wings (Code Bracket shapes) -->
            <path class="bug-wings" d="M95 95 C60 90 45 120 50 155 C65 170 90 155 100 145 Z" fill="#4F46E5" opacity="0.8"></path>
            <path class="bug-wings" d="M145 95 C180 90 195 120 190 155 C175 170 150 155 140 145 Z" fill="#4F46E5" opacity="0.8"></path>
            
            <!-- Antennae -->
            <path class="bug-antenna-l" d="M110 55 Q90 25 65 40" stroke="#111827" stroke-width="8" stroke-linecap="round"></path>
            <path class="bug-antenna-r" d="M130 55 Q150 25 175 40" stroke="#111827" stroke-width="8" stroke-linecap="round"></path>
            
            <!-- Bug Legs -->
            <path d="M50 100 L95 110 M190 100 L145 110 M45 130 L95 130 M195 130 L145 130" stroke="#111827" stroke-width="6" stroke-linecap="round" opacity="0.5"></path>
        </svg>
        <svg v-show="isDarkMode" viewBox="0 0 240 240" class="w-40 h-40 mb-6 drop-shadow-[0_0_15px_rgba(16,185,129,0.3)]" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Hexagon Shield (Emerald 400) -->
            <path d="M120 15 L210 65 L210 175 L120 225 L30 175 L30 65 Z" stroke="#34D399" stroke-width="12" stroke-linejoin="round"></path>
            
            <!-- Bug Body / I Shape -->
            <rect x="108" y="85" width="24" height="60" rx="12" fill="#F9FAFB"></rect>
            <circle cx="120" cy="65" r="14" fill="#F9FAFB"></circle>
            
            <!-- Bug Wings (Emerald 500) -->
            <path class="bug-wings" d="M95 95 C60 90 45 120 50 155 C65 170 90 155 100 145 Z" fill="#10B981" opacity="0.9"></path>
            <path class="bug-wings" d="M145 95 C180 90 195 120 190 155 C175 170 150 155 140 145 Z" fill="#10B981" opacity="0.9"></path>
            
            <!-- Antennae -->
            <path class="bug-antenna-l" d="M110 55 Q90 25 65 40" stroke="#F9FAFB" stroke-width="8" stroke-linecap="round"></path>
            <path class="bug-antenna-r" d="M130 55 Q150 25 175 40" stroke="#F9FAFB" stroke-width="8" stroke-linecap="round"></path>
            
            <!-- Bug Legs -->
            <path d="M50 100 L95 110 M190 100 L145 110 M45 130 L95 130 M195 130 L145 130" stroke="#F9FAFB" stroke-width="6" stroke-linecap="round" opacity="0.5"></path>
        </svg>
    </div>
    <h1 v-if="showTitle">Bug <span class="text-primary">I Am</span></h1>
</template>
<script setup lang="ts">
    import { Dark } from 'quasar';
    import { computed } from 'vue';

    defineOptions({
        inheritAttrs: false,
    });

    type LogoSize = 'sm' | 'md' | 'lg';

    const props = withDefaults(
        defineProps<{
            showTitle?: boolean;
            size?: LogoSize;
        }>(),
        {
            showTitle: false,
            size: 'sm',
        },
    );

    const isDarkMode = computed(() => Dark.isActive);

    const showTitle = computed(() => props.showTitle);

    const classes = computed(() => {
        return {
            'size-sm': props.size === 'sm',
            'size-md': props.size === 'md',
            'size-lg': props.size === 'lg'
        };
    });
</script>
<style scoped>    
    .logo-container {
        aspect-ratio: 1 / 1;
        display: flex;
        justify-content: center;
        margin: 0;

        &.size-sm {
            height: 40px;
        }

        &.size-md {
            height: 80px;
        }

        &.size-lg {
            height: 160px;
        }

        &:hover {
            .bug-wings {
                transform: translateY(-2px) scale(1.05);
            }
            
            .bug-antenna-l {
                transform: rotate(-10deg);
            }

            .bug-antenna-r {
                transform: rotate(10deg);
            }
        }
    }

    .bug-wings, .bug-antenna-l, .bug-antenna-r {
        transition: transform 0.3s ease-in-out;
    }    

    .bug-wings {
        transform-origin: center;
    }

    .bug-antenna-l {
        transform-origin: bottom right;
    }

    .bug-antenna-r {
        transform-origin: bottom left;
    }
</style>
